import React, { useState, useEffect, useMemo } from "react";
import api from "../../../services/api";
import "./FilteredReceiptsTable.css";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CommentModal from './components/CommentModal';

const COLUMN_TYPE = "COLUMN";

const DEFAULT_COLUMNS = [
  { key: "route_number", label: "Маршрутный лист", visible: true },
  { key: "driver", label: "Водитель", visible: true },
  { key: "loader1", label: "Грузчик 1", visible: true },
  { key: "loader2", label: "Грузчик 2", visible: true },
  { key: "receipt_number", label: "\u2116 квитанции", visible: true },
  { key: "contract_number", label: "\u2116 договора", visible: true },
  { key: "time", label: "Время", visible: true },
  { key: "delivery_time", label: "Время доставки", visible: true },
  { key: "delivery_address", label: "Адрес доставки", visible: true },
  { key: "client_name", label: "Клиент", visible: true },
  { key: "amount", label: "Сумма", visible: true },
  { key: "to_pay", label: "К оплате", visible: true },
  { key: "paid", label: "Оплачено", visible: true },
  { key: "payment_type", label: "Тип оплаты", visible: true },
  { key: "status", label: "Статус", visible: true },
  { key: "reason", label: "Причина", visible: true },
  { key: "dispatcher_comment", label: "Коммент. дисп.", visible: true },
  { key: "is_check_issued", label: "Чек", visible: true },
  { key: "scannedCount", label: "Скан", visible: true },
  { key: "products", label: "Товары", visible: true }
];

const STATUS_OPTIONS = [
  { label: "Не доставленные", value: "Не доставлено" },

  { label: "Открытые", value: "Открыта" },
  { label: "Закрытые", value: "Закрыта" }
];

const STATUS_COLORS = {
  "Открыта": "#007bff",
  "Закрыта": "#28a745",
  "Не доставлено": "#dc3545"
};

const FilteredReceiptsTable = () => {
  const today = new Date();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [routes, setRoutes] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Не доставлено");
  const [columns, setColumns] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const [scale, setScale] = useState(100);

  const [modalReceiptId, setModalReceiptId] = useState(null);
  


  useEffect(() => {
    const saved = localStorage.getItem("filteredColumns");
    try {
      const parsed = JSON.parse(saved);
      const merged = parsed ? [
        ...parsed,
        ...DEFAULT_COLUMNS.filter((col) => !parsed.some((p) => p.key === col.key))
      ] : DEFAULT_COLUMNS;
      setColumns(merged);
    } catch {
      setColumns(DEFAULT_COLUMNS);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("filteredColumns", JSON.stringify(columns));
  }, [columns]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const res = await api.get("/routes/by-date", {
          params: {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0]
          }
        });
        setRoutes(res || []);
      } catch (err) {
        console.error("Ошибка загрузки маршрутов", err);
      }
    };
    fetchRoutes();
  }, [startDate, endDate]);

  const allReceipts = useMemo(() => {
    return routes.flatMap((route) =>
      route.receipts?.map((receipt) => ({
        ...receipt,
        route_number: route.route_number,
        driver: route.driver,
        loader1: route.loader1,
        loader2: route.loader2
      })) || []
    );
  }, [routes]);

  const filtered = allReceipts.filter((r) => r.status === selectedStatus);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn]?.toString().toLowerCase() || "";
    const valB = b[sortColumn]?.toString().toLowerCase() || "";
    return sortOrder === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const handleSort = (key) => {
    if (sortColumn === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : prev === "desc" ? null : "asc"));
    } else {
      setSortColumn(key);
      setSortOrder("asc");
    }
  };

  const saveDispatcherComment = async (receiptId, comment) => {
  try {
    await api.patch(`/receipts/${receiptId}`, { dispatcher_comment: comment });
    // обновляем локально routes → receipts
    setRoutes(rs => rs.map(route => ({
      ...route,
      receipts: route.receipts.map(rc =>
        rc.id === receiptId ? { ...rc, dispatcher_comment: comment } : rc
      )
    })));
  } catch (e) {
    console.error('Не удалось сохранить комментарий', e);
  } finally {
    setModalReceiptId(null);
  }
};


  const toggleColumn = (key) => {
    setColumns(cols => cols.map(c => c.key === key ? { ...c, visible: !c.visible } : c));
  };

  const moveColumn = (from, to) => {
    const visCols = columns.filter(c => c.visible);
    const dragKey = visCols[from].key;
    const hoverKey = visCols[to].key;
    const fromIndex = columns.findIndex(c => c.key === dragKey);
    const toIndex = columns.findIndex(c => c.key === hoverKey);
    const newCols = [...columns];
    const [moved] = newCols.splice(fromIndex, 1);
    newCols.splice(toIndex, 0, moved);
    setColumns(newCols);
  };

  const ColumnHeader = ({ index, col }) => {
    const ref = React.useRef();
    const [, drop] = useDrop({
      accept: COLUMN_TYPE,
      hover: (item) => {
        if (item.index !== index) {
          moveColumn(item.index, index);
          item.index = index;
        }
      }
    });

    const [{ isDragging }, drag] = useDrag({
      type: COLUMN_TYPE,
      item: { index },
      collect: monitor => ({ isDragging: monitor.isDragging() })
    });

    drag(drop(ref));

    return (
      <th
        ref={ref}
        style={{ cursor: "grab", opacity: isDragging ? 0.5 : 1 }}
        onClick={() => handleSort(col.key)}
      >
        {col.label} {sortColumn === col.key ? (sortOrder === "asc" ? "▲" : sortOrder === "desc" ? "▼" : "") : ""}
      </th>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="filtered-receipts-container">
        <h2 className="filtered-receipts-title">Квитанции по статусу</h2>

        <div className="filtered-receipts-controls">
          <div className="filtered-receipts-date-picker">
            <label>
              С: <input type="date" value={startDate.toISOString().split("T")[0]} onChange={(e) => setStartDate(new Date(e.target.value))} />
            </label>
            <label>
              По: <input type="date" value={endDate.toISOString().split("T")[0]} onChange={(e) => setEndDate(new Date(e.target.value))} />
            </label>
          </div>
          <div className="scale-control">
  <label>Масштаб:&nbsp;
    <select value={scale} onChange={(e) => setScale(parseInt(e.target.value))}>
      {[80, 90, 100, 110, 120, 130].map((val) => (
        <option key={val} value={val}>{val}%</option>
      ))}
    </select>
  </label>
</div>
          <div className="filtered-receipts-status-filter">
            {STATUS_OPTIONS.map(opt => (
              <button key={opt.value} className={`status-chip ${selectedStatus === opt.value ? "active" : ""}`} onClick={() => setSelectedStatus(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="filtered-receipts-columns">
          {columns.map((col) => (
            <label key={col.key}>
              <input type="checkbox" checked={col.visible} onChange={() => toggleColumn(col.key)} /> {col.label}
            </label>
          ))}
        </div>
    <div className="table-scale-wrapper" style={{ transform: `scale(${scale / 100})`, transformOrigin: 'top left' }}>
      <div className="table-scroll-wrapper">
        <table className="filtered-receipts-table">
          <thead>
            <tr>
              {columns.filter(c => c.visible).map((col, index) => (
                <ColumnHeader key={col.key} index={index} col={col} />
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((receipt) => (
              <tr key={receipt.id}>
                  {columns.filter(c => c.visible).map(col => {
                    // Специально для колонки «dispatcher_comment»
                    if (col.key === 'dispatcher_comment') {
                      return (
                        <td key={col.key}>
                          <div className="comment-cell">
                            <span className="comment-text">
                              {receipt.dispatcher_comment || '—'}
                            </span>
                            <button
                              className="comment-open-btn"
                              onClick={() => setModalReceiptId(receipt.id)}
                            >✎</button>
                          </div>
                        </td>
                      );
                    }

                    // Для всех остальных колонок — старая логика
                    let content;
                    if (col.key === 'products') {
                      content = receipt.products?.map(p => `${p.product_name} x${p.actual_quantity}`).join(', ');
                    } else if (col.key === 'scannedCount') {
                      content = receipt.products?.reduce((acc, p) => acc + (p.marks?.length || 0), 0);
                    } else {
                      content = receipt[col.key] ?? '-';
                    }

                    const tdStyle = col.key === 'status'
                      ? {
                          backgroundColor: STATUS_COLORS[receipt.status],
                          color: '#fff',
                          borderRadius: '5px',
                          padding: '0.3rem 0.5rem',
                          fontWeight: 600
                        }
                      : {};

                    return (
                      <td key={col.key} style={tdStyle}>
                        {content}
                      </td>
                    );
                  })}

              </tr>
            ))}
          </tbody>
        </table>
        </div>
        

        <div className="pagination-controls">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Назад</button>
          <span>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Вперёд</button>
        </div>
      </div>
      </div>
      <CommentModal
        isOpen={modalReceiptId !== null}
        receiptId={modalReceiptId}                   // ← сюда передаём id
        onClose={() => setModalReceiptId(null)}
        onSelect={text => saveDispatcherComment(modalReceiptId, text)}
      />
    </DndProvider>
  );
};

export default FilteredReceiptsTable;